import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import PaymentMethodModal from "../../../components/common/PaymentMethodModal";
import {
  ConfirmationMethodProps,
  ConfirmationMethodResult,
  SelectedPaymentType,
  SnackBarStates,
} from "../../../components/common/PaymentMethodModal/types";
import {
  CashOutAmount,
  ESenderRole,
  UserProfile,
} from "../../../setup/api/services/contract/models";
import { ContractContext } from "../../../providers/contract";
import {
  checkCashoutPayment,
  makeCashOutPayment,
  makeManualCashOutPayment,
  makeOtherContractCashOutPayment,
  manualRefundTransaction,
  refundTransaction,
} from "../../../setup/api/services/payment";
import { ICashoutRequest } from "../../../setup/api/services/payment/models";
import { IUserInfoResponseData } from "../../../setup/api/services/user/models";
import {
  ACTION_UNDER_PROCESSING,
  CANNOT_MAKE_THIS_CALL,
} from "../../contract/constants";
import { useTranslation } from "react-i18next";
import { useSourceDestinationEnabled } from "../../../hooks/useSourceDestinationEnabled";
import { useSourceContract } from "../../../hooks/useSourceContract";
import { getCashoutPayout } from "../../../setup/util/twoPartyContract";
import {
  canRefundBackToSourceContract,
  contractIdsMatch,
  getBackToSourceErrorMessage,
} from "../../../setup/util/backToSourceCashout";

interface INewCashoutModal {
  userType: ESenderRole;
  cashOutAmount: CashOutAmount;
  open: boolean;
  onClose: () => void;
  user: IUserInfoResponseData | Partial<UserProfile> | undefined;
}

const NewCashoutModal = ({
  userType,
  cashOutAmount,
  open,
  onClose,
  user,
}: INewCashoutModal) => {
  const { contract } = useContext(ContractContext);
  const { t } = useTranslation();
  const { isSourceDestinationEnabled } = useSourceDestinationEnabled();

  // Net of Waffy's fee on a two-party transaction, which the backend leaves in the line.
  const payoutAmount = useMemo(
    () => getCashoutPayout(contract, cashOutAmount),
    [contract, cashOutAmount]
  );
  const [amount, setAmount] = useState<number | string>();
  const [gatewayPaymentId, setGatewayPaymentId] = useState<string>();
  const [targetContractDomainId, setTargetContractDomainId] = useState<
    string | undefined
  >();

  const isTargetContractLocked = useMemo(
    () => !!cashOutAmount.settlementRequestId,
    [cashOutAmount.settlementRequestId]
  );

  // Refunding a transfer-funded contract back into the wallet contract the
  // customer paid from. Not offered when the cashout already has a destination
  // contract of its own (a settlement request).
  const isBackToSourceRelevant =
    isSourceDestinationEnabled &&
    !isTargetContractLocked &&
    userType === ESenderRole.CUSTOMER;

  // Comparing the party's reference against `sourceContractId` as-is. Fails
  // today because the two are different id shapes, but it's the cheap path and
  // the only one left once the backend sends them in the same shape.
  const matchesSourceDirectly = useMemo(
    () =>
      isBackToSourceRelevant &&
      canRefundBackToSourceContract(contract, user?.userIdentification),
    [isBackToSourceRelevant, contract, user?.userIdentification]
  );

  // Fallback: only look the source contract up when the direct match failed.
  const { sourceContract, isResolvingSourceContract } = useSourceContract(
    isBackToSourceRelevant && !matchesSourceDirectly
      ? contract?.sourceContractId
      : undefined
  );

  // Held back while resolving so the option doesn't flicker in.
  const showBackToSourceOption = useMemo(
    () =>
      matchesSourceDirectly ||
      (isBackToSourceRelevant &&
        !isResolvingSourceContract &&
        canRefundBackToSourceContract(
          contract,
          user?.userIdentification,
          sourceContract
        )),
    [
      matchesSourceDirectly,
      isBackToSourceRelevant,
      isResolvingSourceContract,
      contract,
      user?.userIdentification,
      sourceContract,
    ]
  );

  useEffect(() => {
    if (!cashOutAmount.settlementRequestId || !contract?.settlementRequests)
      return;
    const match = contract.settlementRequests.find(
      (sr) => sr.id === cashOutAmount.settlementRequestId
    );
    if (match) setTargetContractDomainId(match.destinationContractShortId);
  }, [cashOutAmount.settlementRequestId, contract?.settlementRequests, open]);
  const [transactionDate, setTransactionDate] = useState<Date | null>(
    new Date()
  );

  /**
   * The domain payment-api resolves for this cashout. It derives it from the
   * reference transaction, whose id is `<transactionId>-<domainShortId>` —
   * `eQmeXfPt-C6pKUSIY` is a transaction on domain `C6pKUSIY`.
   */
  const transactionDomainId = useMemo(() => {
    const reference = contract?.transactions?.[0]?.referenceTransactionId;
    const separator = reference?.lastIndexOf("-") ?? -1;
    return reference && separator > 0
      ? reference.slice(separator + 1)
      : undefined;
  }, [contract?.transactions]);

  /**
   * `domainId` names the *child* domain a cashout settles, and payment-api only
   * accepts it for "payment on the complex parent, cashout on a milestone": it
   * checks the id is a child of the domain that owns the reference transaction.
   *
   * Being a milestone is not the condition — both sides of the rule are
   * milestones. One is funded through its complex parent (send the id), the
   * other funded by a transfer straight into itself, as back-to-source
   * destinations are (omit it: `contract.shortId` *is* the transaction's domain,
   * and naming it is rejected with "domain Id X does not belong to parent domain
   * Id X"). Where the payment sits is what separates them, so compare the two.
   */
  const cashoutDomainId = useMemo(() => {
    const paidOnParentContract =
      !!contract?.parentId &&
      !!transactionDomainId &&
      !contractIdsMatch(transactionDomainId, contract.shortId);

    return paidOnParentContract ? contract?.shortId : undefined;
  }, [contract?.parentId, contract?.shortId, transactionDomainId]);

  const checkCashoutStatus = useCallback(async () => {
    try {
      await checkCashoutPayment(
        contract?.transactions
          ? contract?.transactions[0]?.referenceTransactionId
          : "",
        String(user?.userIdentification)
      );
      return;
    } catch (err) {
      console.error(err);
    }
  }, [contract, user]);

  const handleSingleViewCashout =
    useCallback(async (): Promise<ConfirmationMethodResult> => {
      let snackBarStates: SnackBarStates | undefined;
      const shouldClose = false;
      let success = false;

      if (!contract || !user) {
        return { shouldClose: false, success: false };
      }

      try {
        const cashoutBody: ICashoutRequest = {
          amount: Number(amount) || 0,
          referenceTransactionId: contract?.transactions
            ? contract?.transactions[0]?.referenceTransactionId
            : "",
          assignedBeneficiaryId: String(user.userIdentification),
          assignedBeneficiaryName: user.profile?.fullName || "",
        };

        const result = await makeCashOutPayment(cashoutBody);
        const isSuccess = result?.data?.status !== "FAILED";

        if (isSuccess) {
          snackBarStates = {
            title: result?.data?.statusDescription || ACTION_UNDER_PROCESSING,
            color: "success",
          };
          await checkCashoutStatus();
          success = true;
        } else {
          snackBarStates = {
            title: result?.data?.statusDescription || ACTION_UNDER_PROCESSING,
            color: "error",
          };
        }
      } catch (error: unknown) {
        snackBarStates = {
          title: CANNOT_MAKE_THIS_CALL,
          color: "error",
        };
        success = false;
      }

      return { snackBarStates, shouldClose, success };
    }, [contract, user, amount, checkCashoutStatus]);

  const handleManualCashout =
    useCallback(async (): Promise<ConfirmationMethodResult> => {
      let snackBarStates: SnackBarStates | undefined;
      const shouldClose = false;
      let success = false;

      if (!contract || !user) {
        return { shouldClose: false, success: false };
      }

      try {
        const cashoutBody: ICashoutRequest = {
          amount: payoutAmount,
          referenceTransactionId: contract?.transactions
            ? contract?.transactions[0]?.referenceTransactionId
            : "",
          assignedBeneficiaryId: String(user.userIdentification),
          assignedBeneficiaryName: user.profile?.fullName || "",
          gatewayPaymentId: String(gatewayPaymentId),
          transactionDate: transactionDate
            ? transactionDate.toISOString()
            : new Date().toISOString(),
        };

        const result = await makeManualCashOutPayment(cashoutBody);
        const isSuccess = result?.data?.status !== "FAILED";

        if (isSuccess) {
          snackBarStates = {
            title: ACTION_UNDER_PROCESSING,
            color: "success",
          };
          success = true;
        } else {
          snackBarStates = {
            title: ACTION_UNDER_PROCESSING,
            color: "error",
          };
        }
      } catch (error: unknown) {
        snackBarStates = {
          title: CANNOT_MAKE_THIS_CALL,
          color: "error",
        };
        success = false;
      }

      return { snackBarStates, shouldClose, success };
    }, [contract, user, gatewayPaymentId, transactionDate, payoutAmount]);

  const handleTabbyRefund = useCallback(
    async (isManual: boolean): Promise<ConfirmationMethodResult> => {
      let snackBarStates: SnackBarStates | undefined;
      const shouldClose = false;
      let success = false;

      if (!contract || !user) {
        return { shouldClose: false, success: false };
      }

      try {
        const cashoutBody: ICashoutRequest = {
          amount: isManual ? payoutAmount : Number(amount) || 0,
          referenceTransactionId: contract?.transactions
            ? contract?.transactions[0]?.referenceTransactionId
            : "",
          domainId: contract.shortId,
          ...(isManual && { gatewayPaymentId: String(gatewayPaymentId) }),
        };

        const result = isManual
          ? await manualRefundTransaction(cashoutBody)
          : await refundTransaction(cashoutBody);

        const isSuccess = result?.data?.status !== "FAILED";

        if (isSuccess) {
          snackBarStates = {
            title: ACTION_UNDER_PROCESSING,
            color: "success",
          };
          success = true;
        } else {
          snackBarStates = {
            title: ACTION_UNDER_PROCESSING,
            color: "error",
          };
        }
      } catch (error: unknown) {
        snackBarStates = {
          title: CANNOT_MAKE_THIS_CALL,
          color: "error",
        };
        success = false;
      }

      return { snackBarStates, shouldClose, success };
    },
    [contract, user, amount, gatewayPaymentId, payoutAmount]
  );

  const handleMoyasarRefund = useCallback(
    async (isManual: boolean): Promise<ConfirmationMethodResult> => {
      let snackBarStates: SnackBarStates | undefined;
      const shouldClose = false;
      let success = false;

      if (!contract || !user) {
        return { shouldClose: false, success: false };
      }

      try {
        const cashoutBody: ICashoutRequest = {
          amount: isManual ? payoutAmount : Number(amount) || 0,
          referenceTransactionId: contract?.transactions
            ? contract?.transactions[0]?.referenceTransactionId
            : "",
          domainId: contract.shortId,
          ...(isManual && { gatewayPaymentId: String(gatewayPaymentId) }),
        };

        const result = isManual
          ? await manualRefundTransaction(cashoutBody)
          : await refundTransaction(cashoutBody);

        const isSuccess = result?.data?.status !== "FAILED";

        if (isSuccess) {
          snackBarStates = {
            title: ACTION_UNDER_PROCESSING,
            color: "success",
          };
          success = true;
        } else {
          snackBarStates = {
            title: ACTION_UNDER_PROCESSING,
            color: "error",
          };
        }
      } catch (error: unknown) {
        snackBarStates = {
          title: CANNOT_MAKE_THIS_CALL,
          color: "error",
        };
        success = false;
      }

      return { snackBarStates, shouldClose, success };
    },
    [contract, user, amount, gatewayPaymentId, payoutAmount]
  );

  const handleNeoRefund = useCallback(
    async (isManual: boolean): Promise<ConfirmationMethodResult> => {
      let snackBarStates: SnackBarStates | undefined;
      const shouldClose = false;
      let success = false;

      if (!contract || !user) {
        return { shouldClose: false, success: false };
      }

      try {
        const cashoutBody: ICashoutRequest = {
          amount: isManual ? payoutAmount : Number(amount) || 0,
          referenceTransactionId: contract?.transactions
            ? contract?.transactions[0]?.referenceTransactionId
            : "",
          domainId: contract.shortId,
          ...(isManual && { gatewayPaymentId: String(gatewayPaymentId) }),
        };

        const result = isManual
          ? await manualRefundTransaction(cashoutBody)
          : await refundTransaction(cashoutBody);

        const isSuccess = result?.data?.status !== "FAILED";

        if (isSuccess) {
          snackBarStates = {
            title: ACTION_UNDER_PROCESSING,
            color: "success",
          };
          success = true;
        } else {
          snackBarStates = {
            title: ACTION_UNDER_PROCESSING,
            color: "error",
          };
        }
      } catch (error: unknown) {
        snackBarStates = {
          title: CANNOT_MAKE_THIS_CALL,
          color: "error",
        };
        success = false;
      }

      return { snackBarStates, shouldClose, success };
    },
    [contract, user, amount, gatewayPaymentId, payoutAmount]
  );

  const handleTamaraRefund = useCallback(
    async (isManual: boolean): Promise<ConfirmationMethodResult> => {
      let snackBarStates: SnackBarStates | undefined;
      const shouldClose = false;
      let success = false;

      if (!contract || !user) {
        return { shouldClose: false, success: false };
      }

      try {
        const cashoutBody: ICashoutRequest = {
          amount: isManual ? payoutAmount : Number(amount) || 0,
          referenceTransactionId: contract?.transactions
            ? contract?.transactions[0]?.referenceTransactionId
            : "",
          domainId: contract.shortId,
          ...(isManual && { gatewayPaymentId: String(gatewayPaymentId) }),
        };

        const result = isManual
          ? await manualRefundTransaction(cashoutBody)
          : await refundTransaction(cashoutBody);

        const isSuccess = result?.data?.status !== "FAILED";

        if (isSuccess) {
          snackBarStates = {
            title: ACTION_UNDER_PROCESSING,
            color: "success",
          };
          success = true;
        } else {
          snackBarStates = {
            title: ACTION_UNDER_PROCESSING,
            color: "error",
          };
        }
      } catch (error: unknown) {
        snackBarStates = {
          title: CANNOT_MAKE_THIS_CALL,
          color: "error",
        };
        success = false;
      }

      return { snackBarStates, shouldClose, success };
    },
    [contract, user, amount, gatewayPaymentId, payoutAmount]
  );

  const handleOtherContractCashout =
    useCallback(async (): Promise<ConfirmationMethodResult> => {
      let snackBarStates: SnackBarStates | undefined;
      const shouldClose = false;
      let success = false;

      if (!contract || !user) {
        return { shouldClose: false, success: false };
      }

      try {
        const cashoutBody: ICashoutRequest = {
          amount: payoutAmount,
          referenceTransactionId: contract?.transactions
            ? contract?.transactions[0]?.referenceTransactionId
            : "",
          assignedBeneficiaryId: String(user.userIdentification),
          assignedBeneficiaryName: user.profile?.fullName || "",
          ...(cashoutDomainId && { domainId: cashoutDomainId }),
          transactionDate: new Date().toISOString(),
          targetContractDomainId: String(targetContractDomainId),
        };

        const result = await makeOtherContractCashOutPayment(cashoutBody);
        const isSuccess = result?.data?.status !== "FAILED";

        if (isSuccess) {
          snackBarStates = {
            title: ACTION_UNDER_PROCESSING,
            color: "success",
          };
          success = true;
        } else {
          snackBarStates = {
            title: ACTION_UNDER_PROCESSING,
            color: "error",
          };
        }
      } catch (error: unknown) {
        snackBarStates = {
          title: CANNOT_MAKE_THIS_CALL,
          color: "error",
        };
        success = false;
      }

      return { snackBarStates, shouldClose, success };
    }, [contract, user, payoutAmount, targetContractDomainId, cashoutDomainId]);

  const handleBackToSourceCashout =
    useCallback(async (): Promise<ConfirmationMethodResult> => {
      let snackBarStates: SnackBarStates | undefined;
      const shouldClose = false;
      let success = false;

      if (!contract || !user) {
        return { shouldClose: false, success: false };
      }

      try {
        // No targetContractDomainId: the backend creates the destination
        // contract from the source contract and transfers into it.
        //
        // `cashoutDomainId` resolves to undefined here in practice — a
        // back-to-source contract is funded by a transfer into itself, so the
        // payment is never on a parent — but it goes through the same rule
        // rather than being hardcoded off.
        const cashoutBody: ICashoutRequest = {
          amount: payoutAmount,
          referenceTransactionId: contract?.transactions
            ? contract?.transactions[0]?.referenceTransactionId
            : "",
          assignedBeneficiaryId: String(user.userIdentification),
          assignedBeneficiaryName: user.profile?.fullName || "",
          ...(cashoutDomainId && { domainId: cashoutDomainId }),
          transactionDate: new Date().toISOString(),
          createBackToSourceContract: true,
        };

        const result = await makeOtherContractCashOutPayment(cashoutBody);
        const isSuccess = result?.data?.status !== "FAILED";

        if (isSuccess) {
          snackBarStates = {
            title: t("lbl_back_to_source_contract_success"),
            color: "success",
          };
          success = true;
        } else {
          snackBarStates = {
            title: result?.data?.statusDescription || ACTION_UNDER_PROCESSING,
            color: "error",
          };
        }
      } catch (error: unknown) {
        snackBarStates = {
          title: getBackToSourceErrorMessage(error, t, CANNOT_MAKE_THIS_CALL),
          color: "error",
        };
        success = false;
      }

      return { snackBarStates, shouldClose, success };
    }, [contract, user, payoutAmount, cashoutDomainId, t]);

  const onConfirm = useCallback(
    async ({
      selectionType,
      isManualRefund = false,
    }: ConfirmationMethodProps): Promise<ConfirmationMethodResult> => {
      if (!contract || !user) {
        return { shouldClose: false, success: false };
      }

      // Handle different payment methods
      if (selectionType === SelectedPaymentType.SINGLE_VIEW) {
        return await handleSingleViewCashout();
      }

      if (selectionType === SelectedPaymentType.BANK) {
        return await handleManualCashout();
      }

      if (
        selectionType === SelectedPaymentType.TABBY &&
        userType === ESenderRole.CUSTOMER
      ) {
        return await handleTabbyRefund(isManualRefund);
      }

      if (
        selectionType === SelectedPaymentType.MOYASAR &&
        userType === ESenderRole.CUSTOMER
      ) {
        return await handleMoyasarRefund(isManualRefund);
      }

      if (
        selectionType === SelectedPaymentType.NEO &&
        userType === ESenderRole.CUSTOMER
      ) {
        return await handleNeoRefund(isManualRefund);
      }

      if (
        selectionType === SelectedPaymentType.TAMARA &&
        userType === ESenderRole.CUSTOMER
      ) {
        return await handleTamaraRefund(isManualRefund);
      }

      if (selectionType === SelectedPaymentType.OTHER_CONTRACT) {
        return await handleOtherContractCashout();
      }

      if (selectionType === SelectedPaymentType.BACK_TO_SOURCE) {
        return await handleBackToSourceCashout();
      }

      return { shouldClose: false, success: false };
    },
    [
      contract,
      user,
      userType,
      handleSingleViewCashout,
      handleManualCashout,
      handleTabbyRefund,
      handleMoyasarRefund,
      handleNeoRefund,
      handleTamaraRefund,
      handleOtherContractCashout,
      handleBackToSourceCashout,
    ]
  );

  return (
    <PaymentMethodModal
      open={open}
      onClose={onClose}
      contract={contract}
      userType={userType}
      userId={user?.userIdentification}
      amount={amount}
      setAmount={setAmount}
      gatewayPaymentId={gatewayPaymentId}
      setGatewayPaymentId={setGatewayPaymentId}
      targetContractDomainId={targetContractDomainId}
      setTargetContractDomainId={setTargetContractDomainId}
      isTargetContractLocked={isTargetContractLocked}
      showBackToSourceOption={showBackToSourceOption}
      showOtherContractOption
      onConfirm={onConfirm}
      paymentAmount={payoutAmount}
      transactionDate={transactionDate}
      setTransactionDate={setTransactionDate}
    />
  );
};

export default NewCashoutModal;
